import { useReducer, useMemo, useCallback, useEffect, useRef, useState } from 'react';
import {
  CharacterData,
  ComputedStats,
  EquipmentItem,
  ClassFeature,
  Modifier,
  ContentReference
} from '../types/character';
import { ExperienceEntry, MarkedTraits } from '../types/levelUp';
import {
  UpdateState,
  PendingOperation,
  createInitialUpdateState,
  createAddDomainCardToLoadoutOperation,
  createMoveDomainCardToVaultOperation,
  createMoveDomainCardToLoadoutOperation,
  createRemoveDomainCardFromVaultLoadoutOperation,
  createAddEquipmentOperation,
  createRemoveEquipmentOperation,
  createEquipItemOperation,
  createUnequipItemOperation,
  // NEW: Reference-based equipment operations
  createAddEquipmentReferenceOperation,
  createRemoveEquipmentReferenceOperation,
  createEquipItemReferenceOperation,
  createUnequipItemReferenceOperation,
  createAddClassFeatureOperation,
  createRemoveClassFeatureOperation,
  createActivateClassFeatureOperation,
  createDeactivateClassFeatureOperation,
  createAddModifierOperation,
  createRemoveModifierOperation,
  createUpdateCharacterInfoOperation,
  createUpdateBaseStatsOperation,
  createUpdateCurrentStateOperation,
  createUpdateManualOverridesOperation,
  createUpdateExperiencesOperation,
  createUpdateInventoryManualItemsOperation,
  createUpdateInventoryDomainCardsOperation,
  createUpdateBiographyOperation,
  createUpdateMarkedTraitsOperation,
  createUpdateTierAdvancementsOperation,
  createUpdateLevelUpHistoryOperation,
  createUpdateMulticlassOperation,
  // Loot & Consumable operations
  createAddLootReferenceOperation,
  createRemoveLootReferenceOperation,
  createUpdateLootReferenceQuantityOperation,
  createAddConsumableReferenceOperation,
  createRemoveConsumableReferenceOperation,
  createUpdateConsumableReferenceQuantityOperation
} from '../types/characterUpdates';
import {
  updateReducer,
  applyPendingOperations,
  createPendingOperation,
  getRetryableOperations,
  validateOperation,
  logOperationQueue
} from '../utils/operationQueue';
import { CharacterStatComputer } from '../utils/CharacterStatComputer';
import { useFirebaseCharacter as useBasicFirebaseCharacter } from './useBasicFirebaseCharacter';
import { deleteField } from '../firebase/firestore';

export interface UseFirebaseCharacterOptions {
  userId: string;
  characterId?: string;
  autoSave?: boolean;
  autoSubscribe?: boolean;
  saveDebounceMs?: number;
  maxRetries?: number;
}

export interface UseFirebaseCharacterResult {
  character: CharacterData | null;
  baseCharacter: CharacterData | null; // For duplicate detection
  computedStats: ComputedStats | null;
  statComputer: CharacterStatComputer | null;
  loading: boolean;
  charactersLoading: boolean;
  saving: boolean;
  error: string | null;
  hasUnsavedChanges: boolean;
  
  // Character management
  createCharacter: (initialData?: Partial<CharacterData>) => Promise<string>;
  loadCharacter: (characterId: string) => Promise<void>;
  deleteCharacter: (characterId: string) => Promise<void>;
  updateCharacterInfo: (updates: Partial<Pick<CharacterData, 'name' | 'class' | 'heritage' | 'level' | 'tier' | 'subclass' | 'subclassCardTier'>>) => void;
  updateBaseStats: (updates: Partial<CharacterData['baseStats']>) => void;
  updateCurrentState: (updates: Partial<CharacterData['currentState']>) => void;
  updateManualOverrides: (updates: Partial<NonNullable<CharacterData['manualOverrides']>>) => void;
  updateExperiences: (experiences: ExperienceEntry[]) => void;
  updateInventoryManualItems: (manualItems: Array<{ name: string; description: string; quantity: number }>) => void;
  updateInventoryDomainCards: (domainCardLoadout: ContentReference[], domainCardVault: ContentReference[]) => void;
  updateBiography: (updates: Partial<CharacterData['biography']>) => void;

  // Level-Up System (Phase 26A)
  updateMarkedTraits: (markedTraits: MarkedTraits) => void;
  updateTierAdvancements: (tierAdvancements: CharacterData['tierAdvancements']) => void;
  updateLevelUpHistory: (levelUpHistory: CharacterData['levelUpHistory']) => void;

  // Multiclass System (Phase 26B)
  updateMulticlass: (multiclass: CharacterData['multiclass']) => void;
  
  // Equipment management (legacy)
  addEquipment: (equipment: EquipmentItem) => void;
  removeEquipment: (equipmentId: string) => void;
  equipItem: (equipmentId: string) => void;
  unequipItem: (equipmentId: string) => void;
  
  // NEW: Reference-based equipment management
  addEquipmentByReference: (itemId: string, quantity?: number) => void;
  removeEquipmentByReference: (itemId: string, quantity?: number) => void;
  equipItemByReference: (itemId: string, count?: number) => void;
  unequipItemByReference: (itemId: string, count?: number) => void;

  // Loot & Consumable management (Phase 2)
  addLootByReference: (itemId: string, quantity?: number) => void;
  removeLootByReference: (itemId: string, quantity?: number) => void;
  updateLootQuantity: (itemId: string, newQuantity: number) => void;
  addConsumableByReference: (itemId: string, quantity?: number) => void;
  removeConsumableByReference: (itemId: string, quantity?: number) => void;
  updateConsumableQuantity: (itemId: string, newQuantity: number) => void;

  // Vault & Loadout System (Phase 48: replaces domain card reference operations)
  addDomainCardToLoadout: (cardId: string, autoVault?: boolean) => void;
  moveDomainCardToVault: (cardId: string) => void;
  moveDomainCardToLoadout: (cardId: string) => void;
  removeDomainCard: (cardId: string) => void;
  
  // Class feature management
  addClassFeature: (feature: ClassFeature) => void;
  removeClassFeature: (featureId: string) => void;
  activateClassFeature: (featureId: string) => void;
  deactivateClassFeature: (featureId: string) => void;
  
  // Manual modifier management
  addModifier: (modifier: Modifier) => void;
  removeModifier: (modifierId: string) => void;
  
  // Firebase operations
  saveCharacter: () => Promise<void>;
  characters: CharacterData[];
  loadUserCharacters: () => Promise<void>;
  
  // Debug utilities
  clearError: () => void;
  retryFailedOperations: () => void;
  getOperationQueueStats: () => any;
}

const DEFAULT_SAVE_DEBOUNCE = 2000; // 2 seconds
const DEFAULT_MAX_RETRIES = 3;

export const useFirebaseCharacter = ({
  userId,
  characterId,
  autoSave = true,
  autoSubscribe = true,
  saveDebounceMs = DEFAULT_SAVE_DEBOUNCE,
  maxRetries = DEFAULT_MAX_RETRIES
}: UseFirebaseCharacterOptions): UseFirebaseCharacterResult => {
  
  // State management
  const [state, dispatch] = useReducer(updateReducer, createInitialUpdateState());
  
  // Firebase integration
  const firebase = useBasicFirebaseCharacter(userId);
  
  // Auto-save timeout ref
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef(false);

  // Reset state when characterId changes
  useEffect(() => {
    dispatch({ type: 'SET_BASE_CHARACTER', character: null });
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
  }, [characterId]);

  // Sync Firebase character to base state
  useEffect(() => {
    if (firebase.character) {
      dispatch({ type: 'SET_BASE_CHARACTER', character: firebase.character });
    }
  }, [firebase.character]);

  // Sync other Firebase state
  useEffect(() => {
    dispatch({ type: 'SET_LOADING', loading: firebase.loading });
  }, [firebase.loading]);

  useEffect(() => {
    dispatch({ type: 'SET_CHARACTERS_LOADING', loading: firebase.charactersLoading });
  }, [firebase.charactersLoading]);

  useEffect(() => {
    dispatch({ type: 'SET_CHARACTERS', characters: firebase.characters });
  }, [firebase.characters]);

  // Compute optimistic character state (base + pending operations)
  const character = useMemo(() => {
    return applyPendingOperations(state.baseCharacter, state.pendingOperations);
  }, [state.baseCharacter, state.pendingOperations]);

  // Compute stats from optimistic character state (async)
  const [computedStats, setComputedStats] = useState<ComputedStats | null>(null);
  const [statComputer, setStatComputer] = useState<CharacterStatComputer | null>(null);
  
  useEffect(() => {
    let isCancelled = false;
    
    const computeStatsAsync = async () => {
      if (!character) {
        setComputedStats(null);
        setStatComputer(null);
        return;
      }
      
      try {
        const computer = new CharacterStatComputer(character);
        const stats = await computer.computeStats();
        
        if (!isCancelled) {
          setComputedStats(stats);
          setStatComputer(computer);
        }
      } catch (err) {
        console.error('Failed to compute stats:', err);
        if (!isCancelled) {
          setComputedStats(null);
          setStatComputer(null);
        }
      }
    };
    
    computeStatsAsync();
    
    return () => {
      isCancelled = true;
    };
  }, [character]);

  // Auto-save functionality - uses ref to access current state
  const stateRef = useRef(state);
  stateRef.current = state;

  /**
   * Build Firebase update object with deleteField() for removed manual overrides
   * Compares baseCharacter overrides vs finalState overrides to detect deletions
   */
  const buildManualOverridesUpdate = (
    baseOverrides: CharacterData['manualOverrides'],
    finalOverrides: CharacterData['manualOverrides']
  ): Record<string, any> => {
    const updates: Record<string, any> = {};
    const base = baseOverrides || {};
    const final = finalOverrides || {};

    // Fields to check (all override fields except thresholds which are now flat)
    const fields: Array<keyof Omit<NonNullable<CharacterData['manualOverrides']>, 'thresholdMajor' | 'thresholdSevere'>> =
      ['evasion', 'armor', 'hpSlots', 'stressSlots', 'hope'];

    // Check each top-level override field
    for (const field of fields) {
      if (field in base && !(field in final)) {
        // Field was removed - use deleteField()
        updates[`manualOverrides.${field}`] = deleteField();
      }
    }

    // Check threshold fields (they're flat now)
    if ('thresholdMajor' in base && !('thresholdMajor' in final)) {
      updates['manualOverrides.thresholdMajor'] = deleteField();
    }
    if ('thresholdSevere' in base && !('thresholdSevere' in final)) {
      updates['manualOverrides.thresholdSevere'] = deleteField();
    }

    // If entire manualOverrides was removed (no overrides remain)
    if (Object.keys(final).length === 0 && Object.keys(base).length > 0) {
      updates.manualOverrides = deleteField();
    }

    return updates;
  };

  const flushOperations = useCallback(async (): Promise<void> => {
    const currentState = stateRef.current;
    
    if (isSavingRef.current || currentState.pendingOperations.length === 0 || !currentState.baseCharacter?.id) {
      return;
    }

    isSavingRef.current = true;
    dispatch({ type: 'SET_SAVING', saving: true });

    try {
      // Get retryable operations (handles exponential backoff)
      const operationsToSave = getRetryableOperations(currentState.pendingOperations, maxRetries);
      
      if (operationsToSave.length === 0) {
        console.log('No retryable operations found');
        return;
      }

      // Apply all operations to get final state
      const finalState = applyPendingOperations(currentState.baseCharacter, operationsToSave);
      if (!finalState) {
        throw new Error('Failed to compute final character state');
      }

      // Validate the final state before saving
      for (const operation of operationsToSave) {
        const validation = validateOperation(operation, currentState.baseCharacter);
        if (!validation.valid) {
          console.error(`Operation ${operation.id} failed validation:`, validation.error);
          dispatch({ type: 'REMOVE_OPERATION', operationId: operation.id });
          continue;
        }
      }

      console.log(`Saving ${operationsToSave.length} operations to Firebase...`);
      logOperationQueue(operationsToSave);

      // Save to Firebase - clean undefined values
      const { id, ...dataToSave } = finalState;
      
      // Recursively remove undefined values to prevent Firebase errors
      const cleanData = (obj: any): any => {
        if (obj === null || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(cleanData);
        
        const cleaned: any = {};
        for (const [key, value] of Object.entries(obj)) {
          if (value !== undefined) {
            cleaned[key] = cleanData(value);
          }
        }
        return cleaned;
      };
      
      const cleanedData = cleanData(dataToSave);

      // Build delete operations for removed manual overrides
      const overrideDeletes = buildManualOverridesUpdate(
        currentState.baseCharacter.manualOverrides,
        finalState.manualOverrides
      );

      // Merge cleaned data with delete operations (deleteField takes precedence)
      const updates = { ...cleanedData, ...overrideDeletes };

      await firebase.updateCharacter(updates);

      // Mark operations as saved
      const savedOperationIds = operationsToSave.map(op => op.id);
      dispatch({ type: 'OPERATIONS_SAVED', clearedOperationIds: savedOperationIds });

      console.log('✅ Auto-save completed successfully');

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Auto-save failed';
      console.error('❌ Auto-save failed:', errorMessage);
      
      dispatch({ type: 'SAVE_FAILED', error: errorMessage });

      // Increment retry count for failed operations
      currentState.pendingOperations.forEach(op => {
        dispatch({ type: 'RETRY_OPERATION', operationId: op.id });
      });
    } finally {
      isSavingRef.current = false;
      dispatch({ type: 'SET_SAVING', saving: false });
    }
  }, [firebase.updateCharacter, maxRetries]);

  // Debounced auto-save
  const scheduleAutoSave = useCallback(() => {
    if (!autoSave || !state.baseCharacter?.id) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Schedule new save - use a function that will capture current state when executed
    saveTimeoutRef.current = setTimeout(async () => {
      // Call flushOperations directly to get current state at execution time
      try {
        await flushOperations();
      } catch (error) {
        console.error('Scheduled auto-save failed:', error);
      }
      saveTimeoutRef.current = null;
    }, saveDebounceMs);
  }, [autoSave, state.baseCharacter?.id, flushOperations, saveDebounceMs]);

  // Generic operation queuing function
  const queueOperation = useCallback((operationTemplate: Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'>) => {
    if (!character) {
      console.warn('Cannot queue operation: no character loaded');
      return;
    }

    const operation = createPendingOperation(operationTemplate);
    dispatch({ type: 'QUEUE_OPERATION', operation });
    
    // Schedule auto-save
    scheduleAutoSave();
  }, [character, scheduleAutoSave]);

  // Character management functions
  const createCharacter = useCallback(async (initialData?: Partial<CharacterData>): Promise<string> => {
    return firebase.createCharacter(initialData);
  }, [firebase.createCharacter]);

  const loadCharacter = useCallback(async (characterId: string): Promise<void> => {
    return firebase.loadCharacter(characterId);
  }, [firebase.loadCharacter]);

  const deleteCharacter = useCallback(async (characterId: string): Promise<void> => {
    return firebase.deleteCharacter(characterId);
  }, [firebase.deleteCharacter]);

  const updateCharacterInfo = useCallback((updates: Partial<Pick<CharacterData, 'name' | 'class' | 'heritage' | 'level' | 'tier' | 'subclass' | 'subclassCardTier'>>) => {
    queueOperation(createUpdateCharacterInfoOperation(updates));
  }, [queueOperation]);

  const updateBaseStats = useCallback((updates: Partial<CharacterData['baseStats']>) => {
    queueOperation(createUpdateBaseStatsOperation(updates));
  }, [queueOperation]);

  const updateCurrentState = useCallback((updates: Partial<CharacterData['currentState']>) => {
    queueOperation(createUpdateCurrentStateOperation(updates));
  }, [queueOperation]);

  const updateManualOverrides = useCallback((updates: Partial<NonNullable<CharacterData['manualOverrides']>>) => {
    queueOperation(createUpdateManualOverridesOperation(updates));
  }, [queueOperation]);

  const updateExperiences = useCallback((experiences: ExperienceEntry[]) => {
    queueOperation(createUpdateExperiencesOperation(experiences));
  }, [queueOperation]);

  const updateInventoryManualItems = useCallback((manualItems: Array<{ name: string; description: string; quantity: number }>) => {
    queueOperation(createUpdateInventoryManualItemsOperation(manualItems));
  }, [queueOperation]);

  const updateInventoryDomainCards = useCallback((domainCardLoadout: ContentReference[], domainCardVault: ContentReference[]) => {
    queueOperation(createUpdateInventoryDomainCardsOperation(domainCardLoadout, domainCardVault));
  }, [queueOperation]);

  const updateBiography = useCallback((updates: Partial<CharacterData['biography']>) => {
    queueOperation(createUpdateBiographyOperation(updates));
  }, [queueOperation]);

  // Level-Up System (Phase 26A)
  const updateMarkedTraits = useCallback((markedTraits: MarkedTraits) => {
    queueOperation(createUpdateMarkedTraitsOperation(markedTraits));
  }, [queueOperation]);

  const updateTierAdvancements = useCallback((tierAdvancements: CharacterData['tierAdvancements']) => {
    queueOperation(createUpdateTierAdvancementsOperation(tierAdvancements));
  }, [queueOperation]);

  const updateLevelUpHistory = useCallback((levelUpHistory: CharacterData['levelUpHistory']) => {
    queueOperation(createUpdateLevelUpHistoryOperation(levelUpHistory));
  }, [queueOperation]);

  // Multiclass System (Phase 26B)
  const updateMulticlass = useCallback((multiclass: CharacterData['multiclass']) => {
    queueOperation(createUpdateMulticlassOperation(multiclass));
  }, [queueOperation]);

  // Equipment management
  const addEquipment = useCallback((equipment: EquipmentItem) => {
    queueOperation(createAddEquipmentOperation(equipment));
  }, [queueOperation]);

  const removeEquipment = useCallback((equipmentId: string) => {
    queueOperation(createRemoveEquipmentOperation(equipmentId));
  }, [queueOperation]);

  const equipItem = useCallback((equipmentId: string) => {
    queueOperation(createEquipItemOperation(equipmentId));
  }, [queueOperation]);

  const unequipItem = useCallback((equipmentId: string) => {
    queueOperation(createUnequipItemOperation(equipmentId));
  }, [queueOperation]);

  // NEW: Reference-based equipment management
  const addEquipmentByReference = useCallback((itemId: string, quantity: number = 1) => {
    queueOperation(createAddEquipmentReferenceOperation(itemId, quantity));
  }, [queueOperation]);

  const removeEquipmentByReference = useCallback((itemId: string, quantity: number = 1) => {
    queueOperation(createRemoveEquipmentReferenceOperation(itemId, quantity));
  }, [queueOperation]);

  const equipItemByReference = useCallback((itemId: string, count: number = 1) => {
    queueOperation(createEquipItemReferenceOperation(itemId, count));
  }, [queueOperation]);

  const unequipItemByReference = useCallback((itemId: string, count: number = 1) => {
    queueOperation(createUnequipItemReferenceOperation(itemId, count));
  }, [queueOperation]);

  // Loot & Consumable management
  const addLootByReference = useCallback((itemId: string, quantity: number = 1) => {
    queueOperation(createAddLootReferenceOperation(itemId, quantity));
  }, [queueOperation]);

  const removeLootByReference = useCallback((itemId: string, quantity: number = 1) => {
    queueOperation(createRemoveLootReferenceOperation(itemId, quantity));
  }, [queueOperation]);

  const updateLootQuantity = useCallback((itemId: string, newQuantity: number) => {
    queueOperation(createUpdateLootReferenceQuantityOperation(itemId, newQuantity));
  }, [queueOperation]);

  const addConsumableByReference = useCallback((itemId: string, quantity: number = 1) => {
    queueOperation(createAddConsumableReferenceOperation(itemId, quantity));
  }, [queueOperation]);

  const removeConsumableByReference = useCallback((itemId: string, quantity: number = 1) => {
    queueOperation(createRemoveConsumableReferenceOperation(itemId, quantity));
  }, [queueOperation]);

  const updateConsumableQuantity = useCallback((itemId: string, newQuantity: number) => {
    queueOperation(createUpdateConsumableReferenceQuantityOperation(itemId, newQuantity));
  }, [queueOperation]);

  // Vault & Loadout System (Phase 48: replaces domain card reference operations)
  const addDomainCardToLoadout = useCallback((cardId: string, autoVault: boolean = true) => {
    queueOperation(createAddDomainCardToLoadoutOperation(cardId, autoVault));
  }, [queueOperation]);

  const moveDomainCardToVault = useCallback((cardId: string) => {
    queueOperation(createMoveDomainCardToVaultOperation(cardId));
  }, [queueOperation]);

  const moveDomainCardToLoadout = useCallback((cardId: string) => {
    queueOperation(createMoveDomainCardToLoadoutOperation(cardId));
  }, [queueOperation]);

  const removeDomainCard = useCallback((cardId: string) => {
    queueOperation(createRemoveDomainCardFromVaultLoadoutOperation(cardId));
  }, [queueOperation]);

  // Class feature management
  const addClassFeature = useCallback((feature: ClassFeature) => {
    queueOperation(createAddClassFeatureOperation(feature));
  }, [queueOperation]);

  const removeClassFeature = useCallback((featureId: string) => {
    queueOperation(createRemoveClassFeatureOperation(featureId));
  }, [queueOperation]);

  const activateClassFeature = useCallback((featureId: string) => {
    queueOperation(createActivateClassFeatureOperation(featureId));
  }, [queueOperation]);

  const deactivateClassFeature = useCallback((featureId: string) => {
    queueOperation(createDeactivateClassFeatureOperation(featureId));
  }, [queueOperation]);

  // Manual modifier management
  const addModifier = useCallback((modifier: Modifier) => {
    queueOperation(createAddModifierOperation(modifier));
  }, [queueOperation]);

  const removeModifier = useCallback((modifierId: string) => {
    queueOperation(createRemoveModifierOperation(modifierId));
  }, [queueOperation]);

  // Manual save
  const saveCharacter = useCallback(async (): Promise<void> => {
    return flushOperations();
  }, [flushOperations]);

  // Debug and utility functions
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const retryFailedOperations = useCallback(() => {
    scheduleAutoSave();
  }, [scheduleAutoSave]);

  const getOperationQueueStats = useCallback(() => {
    return {
      pending: state.pendingOperations.length,
      byType: state.pendingOperations.reduce((acc, op) => {
        acc[op.type] = (acc[op.type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      withRetries: state.pendingOperations.filter(op => op.retryCount > 0).length
    };
  }, [state.pendingOperations]);

  // Auto-load character when characterId changes
  useEffect(() => {
    if (!characterId) return;
    firebase.loadCharacter(characterId).catch(console.error);
  }, [firebase.loadCharacter, characterId]);

  // Real-time subscriptions (optional)
  useEffect(() => {
    if (!autoSubscribe || !characterId) return;
    
    const unsubscribe = firebase.subscribeToCurrentCharacter(characterId);
    return unsubscribe;
  }, [firebase.subscribeToCurrentCharacter, characterId, autoSubscribe]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    character,
    baseCharacter: state.baseCharacter, // Expose for duplicate detection
    computedStats,
    statComputer,
    loading: state.loading,
    charactersLoading: state.charactersLoading,
    saving: state.saving || firebase.saving,
    error: state.error || firebase.error,
    hasUnsavedChanges: state.pendingOperations.length > 0,
    
    // Character management
    createCharacter,
    loadCharacter,
    deleteCharacter,
    updateCharacterInfo,
    updateBaseStats,
    updateCurrentState,
    updateManualOverrides,
    updateExperiences,
    updateInventoryManualItems,
    updateInventoryDomainCards,
    updateBiography,

    // Level-Up System (Phase 26A)
    updateMarkedTraits,
    updateTierAdvancements,
    updateLevelUpHistory,

    // Multiclass System (Phase 26B)
    updateMulticlass,

    // Equipment management (legacy)
    addEquipment,
    removeEquipment,
    equipItem,
    unequipItem,
    
    // NEW: Reference-based equipment management
    addEquipmentByReference,
    removeEquipmentByReference,
    equipItemByReference,
    unequipItemByReference,

    // Loot & Consumable management (Phase 2)
    addLootByReference,
    removeLootByReference,
    updateLootQuantity,
    addConsumableByReference,
    removeConsumableByReference,
    updateConsumableQuantity,

    // Vault & Loadout System (Phase 48: replaces domain card reference operations)
    addDomainCardToLoadout,
    moveDomainCardToVault,
    moveDomainCardToLoadout,
    removeDomainCard,
    
    // Class feature management
    addClassFeature,
    removeClassFeature,
    activateClassFeature,
    deactivateClassFeature,
    
    // Manual modifier management
    addModifier,
    removeModifier,
    
    // Firebase operations
    saveCharacter,
    characters: state.characters,
    loadUserCharacters: firebase.loadUserCharacters,
    
    // Debug utilities
    clearError,
    retryFailedOperations,
    getOperationQueueStats,
  };
};